/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Linking } from 'react-native';
import type { ViewStyle } from 'react-native';
import TabView from '@airasia-phoenix/react-native/components/TabView';
import { TabViewStyle } from '@airasia-phoenix/react-native/components/TabView';
import type { TabsViewProps } from '@airasia-phoenix/react-native/components/TabView';
import { useLogger } from '@airasia-rn/core-logger';
import { useAppConfig, useDeviceInfo } from '@airasia-rn/ui';
import { styles as styles } from './SearchWidgetView.styles';
import { FlightSearchPage } from '@airasia-rn/flights';
import {
  HotelConfigProvider,
  HotelSearchView,
  SearchFormView as HotelSearchWidget,
  useHotelConfig,
  useHandleHotelSearch,
} from '@airasia-rn/hotels';
import {
  SearchFormView as SnapSearchWidget,
  SnapSearchView,
  useHandleSearch,
} from '@airasia-rn/snap';
import { useSetting } from '@airasia-rn/account';

import type {
  ProductTilesData,
  ProductTile,
} from '../../models/homepageBlockTypes';
import { HomePageEvents, trackSearchTabClick } from '../../analytics/homepage';
import { useSyncedUhfLocale } from '../../hooks/useSyncedUhfLocale';
import { TILE_PRODUCT_IDS } from '../../utils/tileRedirectUrls';
import AnimatedTabIcon from './AnimatedTabIcon';

type SearchWidgetKey = 'flights' | 'hotels' | 'snap';
const SEARCH_WIDGET_HEIGHT_ANIMATION_DURATION_MS = 200;
const DEFAULT_SEARCH_WIDGET_HEIGHT = 160;

const getSearchWidgetKey = (productId?: string): SearchWidgetKey | null => {
  if (
    productId === 'flights' ||
    productId === 'hotels' ||
    productId === 'snap'
  ) {
    return productId;
  }

  return null;
};

const FullScreenHotelSearchView: FC<{ onBackPress?: () => void }> = ({
  onBackPress,
}) => {
  const { config } = useHotelConfig();

  return (
    <HotelConfigProvider
      {...config}
      backHandler={onBackPress ?? config.backHandler}
      searchSource="home"
    >
      <HotelSearchView />
    </HotelConfigProvider>
  );
};

export interface SearchWidgetViewProps {
  searchTabsData: ProductTilesData;
  initialProductId?: string;
  fullScreen?: boolean;
  onBackPress?: () => void;
  onTabSelect?: (tile: ProductTile) => void;
}

const SearchWidgetView: FC<SearchWidgetViewProps> = ({
  searchTabsData,
  initialProductId,
  fullScreen = false,
  onBackPress,
  onTabSelect,
}) => {
  const { isMobileWidth, isMobileBrowser } = useDeviceInfo();
  const isDesktop = !isMobileWidth && !isMobileBrowser;
  const { handleSearch } = useHandleSearch();
  const { handleSearch: handleHotelSearch } = useHandleHotelSearch({
    source: 'home',
  });
  const { logEvent } = useLogger();
  const { config } = useAppConfig();
  const activeLocale = useSyncedUhfLocale(config.locale);

  // `window?.` does not guard an undeclared global during SSR (ReferenceError),
  // and test environments define `window` without `location`; check both.
  const searchParams =
    typeof window !== 'undefined' && window.location
      ? new URLSearchParams(window.location.search)
      : null;
  const promoCode =
    searchParams?.get('promocode') ??
    searchParams?.get('promoCode') ??
    undefined;

  const { value: recentSearchCount } =
    useSetting('snap_recent_search_max_count') || {};

  const visibleTiles = useMemo(() => {
    const tiles = searchTabsData.tiles;
    return tiles.length > 3 ? tiles.slice(0, 3) : tiles;
  }, [searchTabsData.tiles]);

  const initialTile = useMemo(() => {
    return (
      visibleTiles.find((tile) => tile.productId === initialProductId) ??
      visibleTiles[0]
    );
  }, [initialProductId, visibleTiles]);

  const [selectedWidget, setSelectedWidget] = useState<string>(
    initialTile?.productId ?? '',
  );

  // selectedWidget drives the tab highlight (updates immediately on tap);
  // displayedWidget drives which content renders (swaps mid-fade so the
  // outgoing content fully fades out before the incoming one fades in).
  const [displayedWidget, setDisplayedWidget] = useState<string>(
    initialTile?.productId ?? '',
  );
  const [contentHeight, setContentHeight] = useState(
    DEFAULT_SEARCH_WIDGET_HEIGHT,
  );
  const [hasMeasuredContent, setHasMeasuredContent] = useState(false);
  const tabHeightsRef = useRef<Partial<Record<SearchWidgetKey, number>>>({});
  const selectedWidgetRef = useRef(selectedWidget);
  selectedWidgetRef.current = selectedWidget;

  const visibleSearchWidgetKeys = useMemo<SearchWidgetKey[]>(() => {
    return visibleTiles
      .map((tile) => getSearchWidgetKey(tile.productId))
      .filter((widgetId): widgetId is SearchWidgetKey => widgetId !== null);
  }, [visibleTiles]);

  // Enable the height transition only after the first real measurement, so the
  // initial mount snaps to size instead of animating from the default height.
  useEffect(() => {
    if (contentHeight !== DEFAULT_SEARCH_WIDGET_HEIGHT) {
      setHasMeasuredContent(true);
    }
  }, [contentHeight]);

  const contentTransitionStyle = {
    // Intrinsic height until the first client measurement: the fixed default
    // cropped the server-painted form (the box looked cut off) and then jumped
    // to the measured height after hydration. The fixed height only exists to
    // animate tab switches, and by then it equals the auto height.
    height: hasMeasuredContent ? contentHeight : 'auto',
    transitionProperty: 'height',
    transitionDuration: `${
      hasMeasuredContent ? SEARCH_WIDGET_HEIGHT_ANIMATION_DURATION_MS : 0
    }ms`,
    transitionTimingFunction: 'ease-out',
  } as unknown as ViewStyle;

  useEffect(() => {
    const productId = initialTile?.productId;
    if (productId && productId !== selectedWidgetRef.current) {
      setSelectedWidget(productId);
      setDisplayedWidget(productId);

      const widgetId = getSearchWidgetKey(productId);
      const measuredHeight = widgetId && tabHeightsRef.current[widgetId];
      if (measuredHeight) {
        setContentHeight(measuredHeight);
      }
    }
  }, [initialTile?.productId]);

  const handleTabSelect = (item: TabsViewProps) => {
    if (!item.key) return;

    const tabId = item.key;
    if (tabId === selectedWidget) return;

    setSelectedWidget(tabId);

    const widgetId = getSearchWidgetKey(tabId);
    const measuredHeight = widgetId && tabHeightsRef.current[widgetId];
    if (measuredHeight) {
      setContentHeight(measuredHeight);
    }

    setTimeout(() => {
      setDisplayedWidget(tabId);
    }, 120);

    const selectedTile = visibleTiles.find(
      (tile) => tile.productId === item.key,
    );
    if (selectedTile && onTabSelect) {
      onTabSelect(selectedTile);
      const position =
        visibleTiles.findIndex(
          (tile) => tile.productId === selectedTile.productId,
        ) + 1;
      if (position > 0) {
        logEvent(
          HomePageEvents.tap,
          trackSearchTabClick({
            label: `${selectedTile.productId}|${position}`,
          }),
        );
      }
    }
  };

  const mappedTabs = useMemo<TabsViewProps[]>(() => {
    return visibleTiles.map((tile, index) => ({
      key: tile.productId,
      // TabView has no secondary-text slot, so SNAP's attract text is appended
      text:
        tile.productId === TILE_PRODUCT_IDS.SNAP && tile.attractText
          ? `${tile.title} (${tile.attractText})`
          : tile.title,
      startIcon: {
        height: 40,
        width: 40,
        children: (
          <AnimatedTabIcon
            url={tile.iconUrl}
            index={index + 1}
            isDesktop={isDesktop}
            size={40}
          />
        ),
      },
    }));
  }, [visibleTiles, isDesktop]);

  const currentTabIndex = Math.max(
    mappedTabs.findIndex((tab) => tab.key === selectedWidget),
    0,
  );

  const handleTabContentLayout = useCallback(
    (widgetId: SearchWidgetKey, height: number) => {
      if (height <= 0) return;
      tabHeightsRef.current[widgetId] = height;
      if (displayedWidget === widgetId) {
        setContentHeight(height);
      }
    },
    [displayedWidget],
  );

  const renderFlightsWidget = () => (
    <View style={styles.flightsContainer}>
      <FlightSearchPage
        goBack={onBackPress ?? (() => null)}
        onPressHeaderBack={onBackPress}
        navigateToSearchList={(deeplink) => {
          if (deeplink) Linking.openURL(deeplink);
        }}
        deepLinkParams={{
          locale: activeLocale,
          ...(promoCode && { promoCode }),
        }}
        onSearch={() => null}
        onUIReady={() => null}
        isHomeScreen={true}
      />
    </View>
  );

  const renderHotelsWidget = () => (
    <View style={styles.hotelsContainer}>
      <HotelSearchWidget onError={() => null} onSubmit={handleHotelSearch} />
    </View>
  );

  const renderSnapWidget = () => (
    <View style={styles.hotelsContainer}>
      <SnapSearchWidget
        onError={() => null}
        onSubmit={handleSearch}
        recentSearchMaxCount={Number(recentSearchCount)}
      />
    </View>
  );

  const renderSearchWidgetTabContent = (widgetId: SearchWidgetKey) => {
    const widgetContent = {
      flights: renderFlightsWidget,
      hotels: renderHotelsWidget,
      snap: renderSnapWidget,
    }[widgetId]();
    const isDisplayed = displayedWidget === widgetId;

    return (
      <View
        key={widgetId}
        pointerEvents={isDisplayed ? 'auto' : 'none'}
        style={[styles.tabContent, !isDisplayed && styles.tabContentHidden]}
        onLayout={(event) => {
          handleTabContentLayout(widgetId, event.nativeEvent.layout.height);
        }}
      >
        {widgetContent}
      </View>
    );
  };

  if (fullScreen) {
    return (
      <View style={[styles.container, styles.fullScreenContainer]}>
        <View
          style={[
            styles.contentContainer,
            { height: 'auto' },
            styles.fullScreenContentContainer,
          ]}
        >
          {selectedWidget === 'flights' && renderFlightsWidget()}
          {selectedWidget === 'hotels' && (
            <FullScreenHotelSearchView onBackPress={onBackPress} />
          )}
          {selectedWidget === 'snap' && (
            <SnapSearchView backHandler={onBackPress} />
          )}
        </View>
      </View>
    );
  }

  return (
    <View nativeID="search-widget-container" style={styles.container}>
      <View style={styles.tabView}>
        <TabView
          data={mappedTabs}
          peek={8}
          // Content-sized tabs keep labels like "SNAP! (Flight + Hotel)" on one line
          containerWidth="auto"
          tabStyle={TabViewStyle.STANDARD}
          currentTabIndex={currentTabIndex}
          onSelect={handleTabSelect}
        />
      </View>

      <View style={[styles.contentContainer, contentTransitionStyle]}>
        <View style={styles.tabContent}>
          {visibleSearchWidgetKeys.map((widgetId) =>
            renderSearchWidgetTabContent(widgetId),
          )}
        </View>
      </View>
    </View>
  );
};

export default SearchWidgetView;
